import React from 'react';
import { SparkIcon, Spinner, TagColored, TagOverflowRow, Text } from '@spark';
import { getSparkTagVariant } from '@/features/datasets/components/DataSelection/sparkLabelColors';

// Status sub-columns for the NER Evaluations > Samples table, per Figma
// "Multi-task flow" node 3648:16893: each model column splits into
// per-status sub-columns showing an icon + count and per-label tags.
// Rendered through the Spark DataTable grouped-columns API
// (``columns[].children`` + ``renderGroup`` + ``rowHeight``).
const ENTITY_STATUS_COLUMNS = [
    { key: 'success', label: 'Successfully extracted', icon: 'circle-check', tone: 'success' },
    { key: 'partial', label: 'Partial extraction', icon: 'circle-alert-triangle', tone: 'warning' },
    { key: 'error', label: 'Incorrectly detected or missed', icon: 'circle-alert', tone: 'critical' },
];

// Relations have no "partial" outcome (see calculateRelationMetrics), so the
// design's three-column split collapses to success / failed.
const RELATION_STATUS_COLUMNS = [
    { key: 'success', label: 'Successfully extracted', icon: 'circle-check', tone: 'success' },
    { key: 'error', label: 'Failed', icon: 'circle-alert', tone: 'critical' },
];

const TONE_COLORS = {
    success: 'var(--semantic-feedback-success-on-surface)',
    warning: 'var(--semantic-feedback-warning-on-surface)',
    critical: 'var(--semantic-feedback-critical-on-surface)',
};

const COUNT_BY_STATUS = {
    success: (metrics) => metrics?.successful ?? 0,
    partial: (metrics) => metrics?.partial ?? 0,
    error: (metrics) => metrics?.failed ?? 0,
};

/** One status sub-cell: icon + count on top, label tag + "+N" tag below. */
const StatusSubCell = ({ statusColumn, metrics, allEntities }) => {
    const count = COUNT_BY_STATUS[statusColumn.key](metrics);
    const items = metrics?.countItemsByStatus?.[statusColumn.key] || [];

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: '100%',
                overflow: 'hidden',
            }}
        >
            <div
                className="fs-unmask"
                style={{ display: 'flex', alignItems: 'center', gap: 'var(--dimension-spacing-04)' }}
            >
                <span style={{ display: 'flex', color: TONE_COLORS[statusColumn.tone] }}>
                    <SparkIcon slug={statusColumn.icon} size="small" />
                </span>
                <Text variant="base-medium" color="secondary">{count}</Text>
            </div>
            {items.length > 0 && (
                <TagOverflowRow
                    items={items.map((item) => ({
                        key: String(item.label),
                        tag: (
                            <TagColored
                                label={`${String(item.label).toUpperCase()} (${item.count})`}
                                variant={getSparkTagVariant(item.label, allEntities || [])}
                            />
                        ),
                        tooltipText: `${item.label} (${item.count})`,
                    }))}
                />
            )}
        </div>
    );
};

/**
 * Builds one grouped DataTable column for a model: `title` (model name +
 * response filter) over one sub-column per extraction status. While the
 * model is still processing, a spinner spans the whole group.
 *
 * @param {object} options
 * @param {string} options.key            DataTable column key.
 * @param {import('react').ReactNode} options.title  Group header content.
 * @param {boolean} options.isRelations   Relations tab (no "partial" status).
 * @param {string[]} options.allEntities  All label names (stable tag colors).
 * @param {boolean} options.isProcessing  Model evaluation still running.
 * @param {(record: object) => boolean} [options.isEmptyResponse]
 *   Stored model output is missing (``*_empty_in_db``). Spans the group
 *   with "No Response" instead of 0/0/0 status counts.
 * @param {(record: object) => object} options.getMetrics  Record → metrics
 *   ({successful, partial, failed, countItemsByStatus}).
 */
export const buildModelStatusColumn = ({
    key,
    title,
    isRelations,
    allEntities,
    isProcessing,
    isEmptyResponse,
    getMetrics,
}) => {
    // Metrics are derived per record; cache so the sub-cells of one row
    // don't recompute the same breakdown.
    const metricsCache = new WeakMap();
    const metricsFor = (record) => {
        if (!metricsCache.has(record)) {
            metricsCache.set(record, getMetrics(record));
        }
        return metricsCache.get(record);
    };

    const statusColumns = isRelations ? RELATION_STATUS_COLUMNS : ENTITY_STATUS_COLUMNS;

    return {
        key,
        title,
        renderGroup: (record) => {
            if (isProcessing) {
                return (
                    <div
                        className="fs-unmask"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 'var(--dimension-spacing-08)',
                            width: '100%',
                        }}
                    >
                        <Spinner size="small" />
                        <Text variant="base-regular">Processing...</Text>
                    </div>
                );
            }
            if (isEmptyResponse?.(record)) {
                return (
                    <div
                        className="fs-unmask"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                        }}
                    >
                        <TagColored label="No Response" variant="disabled" />
                    </div>
                );
            }
            return undefined;
        },
        children: statusColumns.map((statusColumn) => ({
            key: `${key}-${statusColumn.key}`,
            title: <span className="fs-unmask">{statusColumn.label}</span>,
            render: (_, record) => (
                <StatusSubCell
                    statusColumn={statusColumn}
                    metrics={metricsFor(record)}
                    allEntities={allEntities}
                />
            ),
        })),
    };
};
